import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { RefreshCcw } from "lucide-react";

const ProjectCard = ({ title, summary, stack = [], details = [], link }) => {
  const [flipped, setFlipped] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleFlip = () => {
    if (!isDesktop) setFlipped((prev) => !prev);
  };

  return (
    <div
      className="relative cursor-pointer group perspective-[1200px]"
      onClick={handleFlip}
      onMouseEnter={() => isDesktop && setFlipped(true)}
      onMouseLeave={() => isDesktop && setFlipped(false)}
    >
      {/* Flip container */}
      <motion.div
        className="relative w-full h-[280px] transition-transform duration-700"
        animate={{ rotateY: flipped ? 180 : 0 }}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* FRONT */}
        <div
          style={{
            transform: "rotateY(0deg)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
          className="absolute inset-0 rounded-2xl border border-white/10 bg-white/[0.03]
                     p-6 hover:border-teal-400/40 hover:shadow-[0_0_20px_rgba(0,224,183,0.15)]
                     transition-all duration-500"
        >
          <div className="flex flex-col justify-between h-full">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
              <p className="text-slate-400 text-sm mb-4">{summary}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {stack.map((tech, i) => (
                <span
                  key={i}
                  className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-slate-400 hover:text-white transition-colors"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* BACK */}
        <div
          style={{
            transform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
          className="absolute inset-0 rounded-2xl border border-white/10 bg-white/[0.03]
                     p-6 hover:border-teal-400/40 hover:shadow-[0_0_20px_rgba(0,224,183,0.15)]
                     transition-all duration-500"
        >
          <div className="flex flex-col justify-between h-full">
            <div className="space-y-2 text-slate-300 text-sm">
              <ul className="list-disc pl-4 space-y-1">
                {details.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 text-sm text-teal-400 hover:text-blue-400 transition-colors"
              >
                View Project →
              </a>
            )}
          </div>
        </div>
      </motion.div>

      {/* Flip indicator */}
      {!isDesktop && (
        <div className="absolute top-3 right-4 text-slate-500 text-xs flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity z-20">
          <span>Tap to flip</span>
          <RefreshCcw className="w-3 h-3" />
        </div>
      )}
    </div>
  );
};

export default ProjectCard;
